import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Helmet } from "react-helmet-async";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import SuccessModal from "containers/BlogPage/BlogModal/SuccessModal";
import FailModal from "containers/BlogPage/BlogModal/FailModal";

// Types & Interfaces
interface ApiError {
  message: string;
  errors?: {
    [key: string]: string[];
  };
}

interface FormData {
  name: string;
  email: string;
  password: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
}

export interface PageSignUpProps {
  className?: string;
}

// Component Definition
const PageSignUp: React.FC<PageSignUpProps> = ({ className = "" }) => {
  // Navigation
  const navigate = useNavigate();

  // State
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Utility Functions
  const getErrorMessage = (error: ApiError): string => {
    if (error.errors) {
      const errorMessages = Object.entries(error.errors).map(([field, messages]) => {
        switch(field) {
          case 'email':
            if (messages.includes('validation.unique')) {
              return '이미 가입된 이메일이에요.';
            }
            return '올바른 이메일 주소를 입력해 주세요.';
          case 'password':
            if (messages.includes('validation.min.string')) {
              return '비밀번호는 6자 이상이어야 해요.';
            }
            return '비밀번호 형식이 올바르지 않아요.';
          case 'name':
            return '이름을 입력해 주세요.';
          default:
            return `${field}: ${messages[0]}`;
        }
      });
      return errorMessages.join('\n');
    }
    return error.message || '알 수 없는 오류가 발생했어요.';
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = "이름을 입력해 주세요.";
      isValid = false;
    }

    if (!formData.email) {
      errors.email = "이메일을 입력해 주세요.";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "올바른 이메일 주소를 입력해 주세요.";
      isValid = false;
    }

    if (!formData.password) {
      errors.password = "비밀번호를 입력해 주세요.";
      isValid = false;
    } else if (formData.password.length < 6) {
      errors.password = "비밀번호는 6자 이상이어야 해요.";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Event Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('https://develop.coursemos.co.kr/api/v1/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw data;
      }

      setFormData({
        name: "",
        email: "",
        password: "",
      });
      setShowSuccessModal(true);
    } catch (err) {
      const apiError = err as ApiError;
      setErrorMessage(getErrorMessage(apiError));
      setShowFailModal(true);

      if (apiError.errors?.email?.includes('validation.unique')) {
        setFormErrors(prev => ({
          ...prev,
          email: '이미 가입된 이메일이에요.',
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <div className={`nc-PageSignUp ${className}`} data-nc-id="PageSignUp">
        <Helmet>
          <title>Coursemos Tech Feed</title>
        </Helmet>
        <div className="container mb-24 lg:mb-32">
          <h2 className="my-20 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
            회원가입
          </h2>
          <div className="max-w-md mx-auto space-y-6">
            <form className="grid grid-cols-1 gap-6" onSubmit={handleSubmit}>
              <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                이름
              </span>
                <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`mt-1 ${formErrors.name ? 'border-red-500' : ''}`}
                    required
                />
                {formErrors.name && (
                    <span className="text-red-500 text-sm mt-1">{formErrors.name}</span>
                )}
              </label>
              <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                이메일
              </span>
                <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="example@example.com"
                    className={`mt-1 ${formErrors.email ? 'border-red-500' : ''}`}
                    required
                />
                {formErrors.email && (
                    <span className="text-red-500 text-sm mt-1">{formErrors.email}</span>
                )}
              </label>
              <label className="block">
              <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                비밀번호
              </span>
                <div className="relative mt-1">
                  <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="6자 이상 입력해 주세요"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={formErrors.password ? 'border-red-500' : ''}
                      required
                  />
                  <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                {formErrors.password && (
                    <span className="text-red-500 text-sm mt-1">{formErrors.password}</span>
                )}
              </label>
              <ButtonPrimary
                  type="submit"
                  disabled={isLoading}
              >
                {isLoading ? "가입 중..." : "가입하기"}
              </ButtonPrimary>
            </form>

            <span className="block text-center text-neutral-700 dark:text-neutral-200">
              이미 계정이 있으신가요? {` `}
              <Link to="/login" className="text-blue-600 font-semibold hover:underline">
                로그인
              </Link>
          </span>
          </div>
        </div>

        <SuccessModal
            isOpen={showSuccessModal}
            onClose={handleSuccessModalClose}
            title="가입 신청 완료"
            mainMessage="승인을 기다리고 있어요."
            subMessage="보통 1~2 영업일 내로 처리돼요."
        />

        <FailModal
            isOpen={showFailModal}
            onClose={() => setShowFailModal(false)}
            title="가입 실패"
            mainMessage="가입에 실패했어요."
            subMessage={errorMessage}
        />
      </div>
  );
};

export default PageSignUp;